import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import { ReportTemplate } from '@team-milva/lab-models/resources'
import type { IReportTemplate } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Read-only lab catalog data (Apps/-lab-management-/ReportTemplates) — MIL-338
// Phase 3, needed to resolve a sent report's display name (template.getDisplayTitle()).
export const useReportTemplateStore = defineStore('reportTemplate', {
  state: () => ({
    allReportTemplates: [] as ReportTemplate[],
    unsubscribeAllReportTemplates: null as (() => void) | null,
  }),

  actions: {
    getReportTemplates(): Promise<ReportTemplate[]> {
      if (this.allReportTemplatesLoaded) {
        return Promise.resolve(this.allReportTemplates)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return resolve([])
        }

        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'ReportTemplates'),
          where('labAccountID', '==', userStore.currentUser.labAccountID)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const templates: ReportTemplate[] = []
          querySnapshot.forEach((docSnap) => {
            templates.push(new ReportTemplate(docSnap.data() as IReportTemplate))
          })
          this.allReportTemplates = templates
          this.unsubscribeAllReportTemplates = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(templates)
          }
        })
      })
    },

    resetStore(): void {
      this.allReportTemplates = []
      this.unsubscribeAllReportTemplates?.()
      this.unsubscribeAllReportTemplates = null
    },
  },

  getters: {
    allReportTemplatesLoaded: (state) => !!state.unsubscribeAllReportTemplates,

    getReportTemplateById:
      (state) =>
      (reportTemplateID: string): ReportTemplate | undefined =>
        state.allReportTemplates.find((x) => x.id === reportTemplateID),
  },
})
